import {
  B2bWithdrawalOrdersFlow,
  B2bWithdrawalStatus,
  B2bWithdrawalType,
} from '@Common/types/bo/b2b/withdrawals';
import { B2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';
import { MdlrPaymentStatus } from '@Common/types/modulr';
import { MdlrAccountTypeValues } from '@Common/types/qa/modulr';

import { makeChoicesCommon } from '@sh4res/bo-common/utils';

export const bankAccountTypeChoices = makeChoicesCommon<(typeof MdlrAccountTypeValues)[number]>()({
  iban: 'resources.bo_common.b2b.withdrawals.bank_account_types.iban',
  intl: 'resources.bo_common.b2b.withdrawals.bank_account_types.intl',
  scan: 'resources.bo_common.b2b.withdrawals.bank_account_types.scan',
  dd: 'resources.bo_common.b2b.withdrawals.bank_account_types.dd',
});

export const b2bWithdrawalTypeChoices = makeChoicesCommon<B2bWithdrawalType>()({
  regular: 'resources.bo_common.b2b.withdrawals.choices.type.regular',
  early: 'resources.bo_common.b2b.withdrawals.choices.type.early',
});

export const b2bWithdrawalStatusChoices = makeChoicesCommon<B2bWithdrawalStatus>()({
  new: 'resources.bo_common.b2b.withdrawals.choices.status.new',
  pending: 'resources.bo_common.b2b.withdrawals.choices.status.pending',
  completed: 'resources.bo_common.b2b.withdrawals.choices.status.completed',
  pending_approval: 'resources.bo_common.b2b.withdrawals.choices.status.pending_approval',
  cancelled: 'resources.bo_common.b2b.withdrawals.choices.status.cancelled',
  settled: 'resources.bo_common.b2b.withdrawals.choices.status.settled',
});

export const b2bWithdrawalOrdersFlowChoices = makeChoicesCommon<B2bWithdrawalOrdersFlow>()({
  regular: 'resources.bo_common.b2b.withdrawals.choices.orders_flow.regular',
  trigger_nav: 'resources.bo_common.b2b.withdrawals.choices.orders_flow.trigger_nav',
  floor_nav: 'resources.bo_common.b2b.withdrawals.choices.orders_flow.floor_nav',
  floor_price: 'resources.bo_common.b2b.withdrawals.choices.orders_flow.floor_price',
});

export const b2bPaymentStatusChoices = makeChoicesCommon<MdlrPaymentStatus>()({
  reprocessing: 'resources.bo_common.b2b.withdrawals.choices.payment_status.reprocessing',
  cancelled: 'resources.bo_common.b2b.withdrawals.choices.payment_status.cancelled',
  void: 'resources.bo_common.b2b.withdrawals.choices.payment_status.void',
  returned: 'resources.bo_common.b2b.withdrawals.choices.payment_status.returned',
  held: 'resources.bo_common.b2b.withdrawals.choices.payment_status.held',
  unallocated: 'resources.bo_common.b2b.withdrawals.choices.payment_status.unallocated',
  submitted: 'resources.bo_common.b2b.withdrawals.choices.payment_status.submitted',
  screening_req: 'resources.bo_common.b2b.withdrawals.choices.payment_status.screening_req',
  validated: 'resources.bo_common.b2b.withdrawals.choices.payment_status.validated',
  pending_for_date: 'resources.bo_common.b2b.withdrawals.choices.payment_status.pending_for_date',
  pending_for_funds: 'resources.bo_common.b2b.withdrawals.choices.payment_status.pending_for_funds',
  ext_proc: 'resources.bo_common.b2b.withdrawals.choices.payment_status.ext_proc',
  processed: 'resources.bo_common.b2b.withdrawals.choices.payment_status.processed',
  reconciled: 'resources.bo_common.b2b.withdrawals.choices.payment_status.reconciled',
  er_invalid: 'resources.bo_common.b2b.withdrawals.choices.payment_status.er_invalid',
  er_extconn: 'resources.bo_common.b2b.withdrawals.choices.payment_status.er_extconn',
  er_extsys: 'resources.bo_common.b2b.withdrawals.choices.payment_status.er_extsys',
  er_expired: 'resources.bo_common.b2b.withdrawals.choices.payment_status.er_expired',
  er_general: 'resources.bo_common.b2b.withdrawals.choices.payment_status.er_general',
  er_batch: 'resources.bo_common.b2b.withdrawals.choices.payment_status.er_batch',
  ext_sent: 'resources.bo_common.b2b.withdrawals.choices.payment_status.ext_sent',
  clearing: 'resources.bo_common.b2b.withdrawals.choices.payment_status.clearing',
  held_in_suspense: 'resources.bo_common.b2b.withdrawals.choices.payment_status.held_in_suspense',
  shares_canceled: 'resources.bo_common.b2b.withdrawals.choices.payment_status.shares_canceled',
});

export const b2bWithdrawalEarlyReasonChoices = makeChoicesCommon<B2BEarlyWithdrawalReason>()({
  marriage: 'resources.bo_common.b2b.withdrawals.choices.early_reason.marriage',
  conclusion_of_a_pacs:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.conclusion_of_a_pacs',
  divorce_or_separation_or_dissolution_of_a_pacs_with_dependent_child_s:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.divorce_or_separation_or_dissolution_of_a_pacs_with_dependent_child_s',
  birth_or_adoption_of_the_3rd_child_then_of_each_subsequent_child:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.birth_or_adoption_of_the_3rd_child_then_of_each_subsequent_child',
  invalidity: 'resources.bo_common.b2b.withdrawals.choices.early_reason.invalidity',
  savers_death: 'resources.bo_common.b2b.withdrawals.choices.early_reason.savers_death',
  death_of_the_pacs_spouse_or_partner:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.death_of_the_pacs_spouse_or_partner',
  over_indebtedness: 'resources.bo_common.b2b.withdrawals.choices.early_reason.over_indebtedness',
  domestic_violence: 'resources.bo_common.b2b.withdrawals.choices.early_reason.domestic_violence',
  creation_or_takeover_of_a_business_by_the_saver_or_his_children_or_his_spouse_or_partner_of_pacs:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.creation_or_takeover_of_a_business_by_the_saver_or_his_children_or_his_spouse_or_partner_of_pacs',
  cessation_of_self_employed_activity:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.cessation_of_self_employed_activity',
  termination_of_the_employment_contract_or_activity_of_individual_entrepreneur:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.termination_of_the_employment_contract_or_activity_of_individual_entrepreneur',
  expiration_of_unemployment_insurance_rights:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.expiration_of_unemployment_insurance_rights',
  leaving_for_retirement:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.leaving_for_retirement',
  acquisition_of_the_main_residence:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.acquisition_of_the_main_residence',
  construction_of_the_main_residence:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.construction_of_the_main_residence',
  expansion_of_the_main_residence:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.expansion_of_the_main_residence',
  rehabilitation_following_a_natural_disaster:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.rehabilitation_following_a_natural_disaster',
  no_employment_contract_or_corporate_mandate:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.no_employment_contract_or_corporate_mandate',
  termination_of_the_social_mandate:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.termination_of_the_social_mandate',
  energy_renovation: 'resources.bo_common.b2b.withdrawals.choices.early_reason.energy_renovation',
  acquisition_of_shares_in_a_production_cooperative:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.acquisition_of_shares_in_a_production_cooperative',
  judicial_liquidation_or_full_transfer_of_the_company:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.judicial_liquidation_or_full_transfer_of_the_company',
  purchase_of_a_low_emission_vehicle:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.purchase_of_a_low_emission_vehicle',
  caregiver_activity_exercised_by_the_holder_or_their_spouse_or_pacs_partner:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.caregiver_activity_exercised_by_the_holder_or_their_spouse_or_pacs_partner',
  option_exercise: 'resources.bo_common.b2b.withdrawals.choices.early_reason.option_exercise',
  instrument_maturity:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.instrument_maturity',
  indirect_management:
    'resources.bo_common.b2b.withdrawals.choices.early_reason.indirect_management',
});
